<template>
  <div>
    <NavBar pageName="Dashboard Templates" />
    <v-container style="margin-top: 185px;">
      <v-btn variant="text" size="small" @click="$router.push('/settings/templates')" class="mb-2">
        <v-icon size="small" class="mr-1">mdi-arrow-left</v-icon>
        Templates Settings
      </v-btn>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>
            <v-icon class="mr-2">mdi-view-dashboard</v-icon>
            Template-uri Dashboard
          </span>
          <div>
            <v-btn color="secondary" variant="outlined" class="mr-2" @click="loadTemplates">
              <v-icon class="mr-1">mdi-refresh</v-icon>
              Refresh
            </v-btn>
            <v-btn color="success" @click="openAddDialog">
              <v-icon class="mr-1">mdi-plus</v-icon>
              Adauga Template
            </v-btn>
          </div>
        </v-card-title>
        <v-card-text>
          <v-data-table
            :headers="headers"
            :items="templates"
            :loading="loading"
            :items-per-page="20"
          >
            <template #[`item.is_default`]="{ item }">
              <v-chip v-if="item.is_default" size="small" color="success">
                <v-icon size="small" class="mr-1">mdi-check</v-icon>
                Default
              </v-chip>
            </template>
            <template #[`item.sections_count`]="{ item }">
              <v-chip size="small" color="primary">
                {{ item.sections_count }} sectiuni
              </v-chip>
            </template>
            <template #[`item.clients_count`]="{ item }">
              <v-chip size="small" :color="item.clients_count > 0 ? 'info' : 'grey'">
                {{ item.clients_count }} clienti
              </v-chip>
            </template>
            <template #[`item.actions`]="{ item }">
              <v-btn size="x-small" color="primary" class="mr-1" @click="openEditDialog(item)">
                <v-icon size="small">mdi-pencil</v-icon>
              </v-btn>
              <v-btn size="x-small" color="info" class="mr-1" @click="openSectionsDialog(item)">
                <v-icon size="small">mdi-view-dashboard-edit</v-icon>
                Sectiuni
              </v-btn>
              <v-btn
                size="x-small"
                color="warning"
                class="mr-1"
                @click="setAsDefault(item)"
                :disabled="item.is_default"
              >
                <v-icon size="small">mdi-star</v-icon>
              </v-btn>
              <v-btn
                size="x-small"
                color="error"
                @click="confirmDelete(item)"
                :disabled="item.is_default || item.clients_count > 0"
              >
                <v-icon size="small">mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Add/Edit Template Dialog -->
      <v-dialog v-model="templateDialog" max-width="500">
        <v-card>
          <v-card-title :class="isEditing ? 'bg-primary' : 'bg-success'" class="text-white">
            <v-icon class="mr-2">{{ isEditing ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
            {{ isEditing ? 'Editeaza Template' : 'Adauga Template Nou' }}
          </v-card-title>
          <v-card-text class="pt-4">
            <v-text-field
              v-model="templateForm.name"
              label="Nume Template"
              :rules="[v => !!v || 'Numele este obligatoriu']"
              density="compact"
              variant="outlined"
              class="mb-3"
            />
            <v-textarea
              v-model="templateForm.description"
              label="Descriere"
              rows="3"
              density="compact"
              variant="outlined"
            />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="templateDialog = false">Anuleaza</v-btn>
            <v-btn
              :color="isEditing ? 'primary' : 'success'"
              @click="saveTemplate"
              :loading="saving"
              :disabled="!templateForm.name"
            >
              {{ isEditing ? 'Salveaza' : 'Adauga' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Sections Configuration Dialog -->
      <v-dialog v-model="sectionsDialog" max-width="900">
        <v-card>
          <v-card-title class="bg-info text-white">
            <v-icon class="mr-2">mdi-view-dashboard-edit</v-icon>
            Configurare Sectiuni: {{ editingTemplate?.name }}
          </v-card-title>
          <v-card-text class="pt-4">
            <v-alert type="info" variant="tonal" density="compact" class="mb-4">
              Trage sectiunile din stanga in dreapta pentru a le adauga in template.
              Reordoneaza-le in lista din dreapta pentru a schimba ordinea de afisare.
            </v-alert>
            <v-row>
              <v-col cols="6">
                <h4 class="mb-2">Sectiuni Disponibile</h4>
                <v-card variant="outlined" class="pa-2" style="min-height: 300px;">
                  <draggable
                    v-model="availableSections"
                    group="sections"
                    item-key="id"
                    class="draggable-list"
                  >
                    <template #item="{ element }">
                      <v-card class="ma-1 pa-2" variant="outlined" style="cursor: grab;">
                        <div class="d-flex align-center">
                          <v-icon size="small" class="mr-2">mdi-drag</v-icon>
                          <div>
                            <div class="font-weight-medium">{{ element.name }}</div>
                            <div class="text-caption text-grey">{{ element.description }}</div>
                          </div>
                          <v-spacer />
                          <v-chip size="x-small" :color="getSectionTypeColor(element.section_type)">
                            {{ element.section_type }}
                          </v-chip>
                        </div>
                      </v-card>
                    </template>
                  </draggable>
                </v-card>
              </v-col>
              <v-col cols="6">
                <h4 class="mb-2">Sectiuni in Template (in ordine)</h4>
                <v-card variant="outlined" color="primary" class="pa-2" style="min-height: 300px;">
                  <draggable
                    v-model="templateSections"
                    group="sections"
                    item-key="id"
                    class="draggable-list"
                  >
                    <template #item="{ element }">
                      <v-card class="ma-1 pa-2" color="primary" variant="tonal" style="cursor: grab;">
                        <div class="d-flex align-center">
                          <v-icon size="small" class="mr-2">mdi-drag</v-icon>
                          <div>
                            <div class="font-weight-medium">{{ element.name }}</div>
                          </div>
                          <v-spacer />
                          <v-chip size="x-small" :color="getSectionTypeColor(element.section_type)">
                            {{ element.section_type }}
                          </v-chip>
                        </div>
                      </v-card>
                    </template>
                  </draggable>
                  <div v-if="templateSections.length === 0" class="text-center pa-4 text-grey">
                    Trage sectiuni aici
                  </div>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="sectionsDialog = false">Anuleaza</v-btn>
            <v-btn color="primary" @click="saveSections" :loading="savingSections">
              Salveaza Sectiuni
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="400">
        <v-card>
          <v-card-title class="bg-error text-white">
            <v-icon class="mr-2">mdi-alert</v-icon>
            Confirma Stergerea
          </v-card-title>
          <v-card-text class="pt-4">
            Sigur doriti sa stergeti template-ul <strong>{{ templateToDelete?.name }}</strong>?
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn @click="deleteDialog = false">Anuleaza</v-btn>
            <v-btn color="error" @click="deleteTemplate" :loading="deleting">Sterge</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Snackbar -->
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000">
        {{ snackbar.message }}
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import NavBar from '@/components/NavBar.vue'
import draggable from 'vuedraggable'
import {
  getDashboardTemplateTypes,
  getDashboardSections,
  createDashboardTemplateType,
  updateDashboardTemplateType,
  updateDashboardTemplateSections,
  deleteDashboardTemplateType,
  setDefaultDashboardTemplate
} from '@/composables/useDashboardTemplatesAPI'
import store from '@/store'

export default {
  name: 'DashboardTemplatesSettings',
  components: { NavBar, draggable },
  data() {
    return {
      loading: false,
      templates: [],
      allSections: [],
      headers: [
        { title: 'ID', key: 'id', width: '60px' },
        { title: 'Nume', key: 'name' },
        { title: 'Descriere', key: 'description' },
        { title: 'Default', key: 'is_default', width: '100px' },
        { title: 'Sectiuni', key: 'sections_count', width: '100px' },
        { title: 'Clienti', key: 'clients_count', width: '100px' },
        { title: 'Actiuni', key: 'actions', width: '220px', sortable: false }
      ],
      // Template dialog
      templateDialog: false,
      isEditing: false,
      saving: false,
      templateForm: {
        id: null,
        name: '',
        description: ''
      },
      // Sections dialog
      sectionsDialog: false,
      savingSections: false,
      editingTemplate: null,
      availableSections: [],
      templateSections: [],
      // Delete dialog
      deleteDialog: false,
      deleting: false,
      templateToDelete: null,
      // Snackbar
      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },
  computed: {
    token() {
      return store.getters.getToken
    }
  },
  async mounted() {
    await this.loadTemplates()
    await this.loadSections()
  },
  methods: {
    async loadTemplates() {
      this.loading = true
      try {
        const result = await getDashboardTemplateTypes(this.token)
        if (!result.error) {
          this.templates = result.template_types || []
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } finally {
        this.loading = false
      }
    },
    async loadSections() {
      const result = await getDashboardSections(this.token)
      if (!result.error) {
        this.allSections = result.sections || []
      }
    },
    openAddDialog() {
      this.isEditing = false
      this.templateForm = { id: null, name: '', description: '' }
      this.templateDialog = true
    },
    openEditDialog(template) {
      this.isEditing = true
      this.templateForm = {
        id: template.id,
        name: template.name,
        description: template.description
      }
      this.templateDialog = true
    },
    async saveTemplate() {
      if (!this.templateForm.name) return

      this.saving = true
      try {
        let result
        if (this.isEditing) {
          result = await updateDashboardTemplateType(
            this.token,
            this.templateForm.id,
            this.templateForm.name,
            this.templateForm.description
          )
        } else {
          result = await createDashboardTemplateType(
            this.token,
            this.templateForm.name,
            this.templateForm.description
          )
        }

        if (!result.error) {
          this.showSnackbar(result.message, 'success')
          this.templateDialog = false
          await this.loadTemplates()
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } finally {
        this.saving = false
      }
    },
    openSectionsDialog(template) {
      this.editingTemplate = template
      // Get sections already in this template
      const templateSectionIds = template.sections.map(s => s.section_id)
      this.templateSections = template.sections.map(s => {
        const fullSection = this.allSections.find(as => as.id === s.section_id)
        return fullSection ? { ...fullSection } : { id: s.section_id, name: s.name }
      })
      // Available sections are those not in the template
      this.availableSections = this.allSections.filter(s => !templateSectionIds.includes(s.id))
      this.sectionsDialog = true
    },
    async saveSections() {
      if (!this.editingTemplate) return

      this.savingSections = true
      try {
        const sectionsData = this.templateSections.map((s, index) => ({
          section_id: s.id,
          order: index
        }))

        const result = await updateDashboardTemplateSections(
          this.token,
          this.editingTemplate.id,
          sectionsData
        )

        if (!result.error) {
          this.showSnackbar(result.message, 'success')
          this.sectionsDialog = false
          await this.loadTemplates()
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } finally {
        this.savingSections = false
      }
    },
    async setAsDefault(template) {
      const result = await setDefaultDashboardTemplate(this.token, template.id)
      if (!result.error) {
        this.showSnackbar(result.message, 'success')
        await this.loadTemplates()
      } else {
        this.showSnackbar(result.message, 'error')
      }
    },
    confirmDelete(template) {
      this.templateToDelete = template
      this.deleteDialog = true
    },
    async deleteTemplate() {
      if (!this.templateToDelete) return

      this.deleting = true
      try {
        const result = await deleteDashboardTemplateType(this.token, this.templateToDelete.id)
        if (!result.error) {
          this.showSnackbar(result.message, 'success')
          this.deleteDialog = false
          await this.loadTemplates()
        } else {
          this.showSnackbar(result.message, 'error')
        }
      } finally {
        this.deleting = false
      }
    },
    getSectionTypeColor(type) {
      const colors = {
        'search': 'primary',
        'stats': 'warning',
        'action': 'success',
        'info': 'info'
      }
      return colors[type] || 'grey'
    },
    showSnackbar(message, color = 'success') {
      this.snackbar = { show: true, message, color }
    }
  }
}
</script>

<style scoped>
.draggable-list {
  min-height: 250px;
}
</style>
